/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import * as PushAudiencesStore    from 'lib/stores/PushAudiencesStore';
import * as SchemaStore           from 'lib/stores/SchemaStore';
import * as PushConstants         from './PushConstants';
import LoaderContainer            from 'components/LoaderContainer/LoaderContainer.react';
import Button                     from 'components/Button/Button.react';
import CategoryList               from 'components/CategoryList/CategoryList.react';
import DashboardView              from 'dashboard/DashboardView.react';
import EmptyState                 from 'components/EmptyState/EmptyState.react';
import FormModal                  from 'components/FormModal/FormModal.react';
import Modal                      from 'components/Modal/Modal.react';
import modalStyles                from 'components/Modal/Modal.scss';
import pluralize                  from 'lib/pluralize';
import history from 'dashboard/history';
import PushAudienceDialog         from 'components/PushAudienceDialog/PushAudienceDialog.react';
import PushAudiencesIndexRow      from './PushAudiencesIndexRow.react';
import queryFromFilters           from 'lib/queryFromFilters';
import React                      from 'react';
import SidebarAction              from 'components/Sidebar/SidebarAction';
import StatusIndicator            from 'components/StatusIndicator/StatusIndicator.react';
import styles                     from 'dashboard/Settings/Settings.scss';
import stylesTable                from 'dashboard/TableView.scss';
import stylesTableColumns         from './PushAudiencesIndexRow.scss';
import subscribeTo                from 'lib/subscribeTo';
import TableHeader                from 'components/Table/TableHeader.react';
import Toolbar                    from 'components/Toolbar/Toolbar.react';
import { formatAudienceSchema }   from 'lib/PushUtils';
import { List, Map }              from 'immutable';
import { SpecialPushes }          from 'lib/Constants';

const XHR_KEY = 'PushAudiencesIndex';

@subscribeTo('Schema', 'schema')
@subscribeTo('PushAudiences', 'pushaudiences')
export default class PushAudiencesIndex extends DashboardView {
  constructor() {
    super();
    this.section = 'Push';
    this.subsection = 'Audiences';
    this.action = new SidebarAction('Create an audience', this.handleCreateAudienceClick.bind(this));
    this.state = {
      availableDevices: [],
      loading: true,
      audiences: new List(),
      showDeleteAudienceModal: false,
      deletionAudienceId: null,
      deleteionAudienceName: null,
      showCreateAudienceModal: false,
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (!nextState && !nextProps) {
      return false;
    }
    return true;
  }

  componentWillMount() {
    this.props.schema.dispatch(SchemaStore.ActionTypes.FETCH);
    this.props.pushaudiences.dispatch(PushAudiencesStore.ActionTypes.FETCH,
      {
        limit: PushConstants.SHOW_MORE_LIMIT,
        min: PushConstants.INITIAL_PAGE_SIZE,
        xhrKey: XHR_KEY,
      }).then(() => {

    });

    this.context.currentApp.fetchAvailableDevices().then(({ available_devices }) => {
      this.setState({
        loading: false,
        availableDevices: available_devices
      });
    }, (error) => {
      this.setState({
        availableDevices: PushConstants.DEFAULT_DEVICES
      });
    });
  }

  componentWillReceiveProps(props) {
    if (props.loaded){
      this.setState({ loading: false});
    }
  }

  componentWillUnmount() {
    this.props.pushaudiences.dispatch(PushAudiencesStore.ActionTypes.ABORT_FETCH, { xhrKey: XHR_KEY});
  }

  handleCreateAudienceClick() {
    this.setState({
      showCreateAudienceModal: true,
    });
  }

  tableData() {
    let schema = formatAudienceSchema(this.props.schema.data.get('classes')) || {};
    let pushAudienceData = this.props.pushaudiences.data;
    let audiences = undefined;

    if (pushAudienceData) {
      audiences = pushAudienceData.get('audiences') || new List();;
    }

    this.schema = schema;
    return audiences;
  }

  handleDelete(objectId, objectName) {
    this.setState({
      showDeleteAudienceModal: true,
      deletionAudienceId: objectId,
      deleteionAudienceName: objectName,
    });
  }

  handleSendPush(objectId) {
    history.push(this.context.generatePath(`push/new?audienceId=${objectId}`));
  }

  renderRow(audience) {
    return (
      <PushAudiencesIndexRow
        key={audience.objectId}
        id={audience.objectId}
        name={audience.name}
        query={audience.query}
        createdAt={new Date(audience.createdAt)}
        schema={this.schema}
        timesUsed={audience.timesUsed}
        onSendPush={this.handleSendPush.bind(this)}
        onDelete={this.handleDelete.bind(this)}/>
    );
  }

  renderToolbar() {
    return (
      <Toolbar
        section='Push'
        subsection='Audiences'>
        <Button color='blue' value='Create an audience' onClick={this.handleCreateAudienceClick.bind(this)} />
      </Toolbar>
    );
  }

  renderHeaders() {
    return (
      <div className={[stylesTableColumns.row, stylesTableColumns.title].join(' ')}>
        <div className={stylesTableColumns.colName}>Name</div>
        <div className={stylesTableColumns.colSize}>Size</div>
        <div className={stylesTableColumns.colCreatedOn}>Created On</div>
        <div className={stylesTableColumns.colPushesSent}># of sends</div>
        <div className={stylesTableColumns.colAction}>Actions</div>
      </div>
    );
  }

  renderEmpty() {
    if (this.state.availableDevices.length === 0) {
      return (
        <EmptyState
          title='No registered devices'
          description='You have no registered installations of your app. You can get started with our Quick Start guide.'
          icon='devices-solid'
          cta='Push Quick Start'
          action={'http://docs.parseplatform.org/parse-server/guide/#push-notifications'} />
      );
    } else {
      return (
        <EmptyState
          title='No push audiences to display yet.'
          icon='users-solid'
          cta='Create your first audience'
          action={() => {
            this.setState({
              showCreateAudienceModal: true
            });
          }} />
      );
    }
  }

  createAudience(modalState, { platforms, name, formattedFilters }){
    let query = {};

    let parseQuery = queryFromFilters('_Installation', formattedFilters);

    if (parseQuery && parseQuery.toJSON()){
      query = parseQuery.toJSON().where || {};
    }

    query.deviceType = { $in: platforms };
    //TODO: handle fail case - need to modify/extend <FormModal> to handle custom footer
    this.props.pushaudiences.dispatch(PushAudiencesStore.ActionTypes.CREATE,{
      query: JSON.stringify(query),
      name,
    }).then(() => {
      this.setState({
        showCreateAudienceModal: false,
      });
    });
  }

  renderContent() {
    let classes = [];
    let toolbar = this.renderToolbar();
    let data = this.tableData();
    let content = null;
    let headers = null;

    let createAudienceModal = this.state.showCreateAudienceModal ? (
      <PushAudienceDialog
        availableDevices={this.state.availableDevices}
        schema={this.schema}
        disableNewSegment={true}
        audienceSize={999999}
        primaryAction={this.createAudience.bind(this,'showCreateModal')}
        secondaryAction={() => {
          this.setState({
            showCreateAudienceModal: false
          });
        }}/>
      ) :
      null;

    let deleteAudienceModal = <FormModal
      icon='warn-outline'
      title='Delete Audience'
      type={Modal.Types.DANGER}
      open={this.state.showDeleteAudienceModal}
      submitText='Delete'
      inProgressText={'Deleting\u2026'}
      onSubmit={() => {
        return this.props.pushaudiences.dispatch(PushAudiencesStore.ActionTypes.DESTROY,
          { objectId : this.state.deletionAudienceId });
      }}
      onSuccess={() => {
        this.setState({
          showDeleteAudienceModal: false,
        });
      }}
      onClose={() => {
        this.setState({ showDeleteAudienceModal: false, });
      }}>
      <p className={modalStyles.only}>Are you sure you want to delete "<strong>{this.state.deleteionAudienceName}</strong>"</p>
    </FormModal>

    if (typeof(data) !== undefined) {
      if (data.size === 0) {
        content = this.renderEmpty();
      } else {
        classes.push(styles.settingsBoxBig);
        content = (
          <div className={stylesTable.rows}>
            {data.map((row) => this.renderRow(row))}
          </div>
        );
        headers = (
          <div>
            {this.renderHeaders()}
          </div>
        );
      }
    }
    let extras = this.renderExtras ? this.renderExtras() : null;

    return (
      <div className={styles.settings_page}>
        {toolbar}
        <div className={styles.holder}>
          <div className={classes.join(' ')}>
            {headers}
            <LoaderContainer close={true} inner={true}>
              {content}
            </LoaderContainer>
          </div>
        </div>
        {deleteAudienceModal}
        {createAudienceModal}
      </div>
    );
  }
}
